import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, MinusIcon, StarIcon, CheckIcon } from 'lucide-react';
import Button from '../../components/ui/Button';
import { useCart } from '../../context/CartContext';
// Mock food item data
const foodItem = {
  id: 'food-1',
  name: 'Fried Egg with Tomato',
  chineseName: '番茄炒鸡蛋',
  price: 7.5,
  rating: 4.8,
  reviewCount: 245,
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rd7ZuDhGdqSuEjdsymkVFA/image.png',
  description: 'A classic Chinese dish featuring fluffy eggs stir-fried with sweet and tangy tomatoes. Served with steamed rice.',
  ingredients: ['Eggs', 'Tomatoes', 'Green onions', 'Garlic', 'Salt', 'Sugar', 'Vegetable oil'],
  nutritionalInfo: {
    calories: 320,
    protein: 15,
    carbs: 12,
    fat: 24
  }
};
const FoodDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    addItem,
    items
  } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [isAdded, setIsAdded] = useState(false);
  const [addAnimation, setAddAnimation] = useState(false);
  // Check if item is already in cart
  useEffect(() => {
    const cartItem = items.find(item => item.id === (id || foodItem.id));
    if (cartItem) {
      setIsAdded(true);
      setQuantity(cartItem.quantity);
    } else {
      setIsAdded(false);
    }
  }, [items, id]);
  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  const handleIncreaseQuantity = () => {
    setQuantity(quantity + 1);
  };
  const handleAddToCart = () => {
    addItem({
      id: foodItem.id,
      name: foodItem.name,
      price: foodItem.price,
      quantity,
      image: foodItem.image,
      chineseName: foodItem.chineseName
    });
    setAddAnimation(true);
    setTimeout(() => {
      setAddAnimation(false);
      setIsAdded(true);
    }, 500);
  };
  return <div className="max-w-4xl mx-auto pb-20 md:pb-0" data-id="element-466">
      <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 mb-4 sm:mb-6 hover:text-[#E84118] transition-colors" aria-label="Back to menu" data-id="element-467">
        <ArrowLeftIcon size={18} className="mr-1" data-id="element-468" />
        <span data-id="element-469">Back to menu</span>
      </button>
      <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-470">
        <div className="md:flex" data-id="element-471">
          {/* Food image */}
          <div className="md:w-1/2" data-id="element-472">
            <img src={foodItem.image} alt={foodItem.name} className="w-full h-56 md:h-full object-cover" data-id="element-473" />
          </div>
          {/* Food details */}
          <div className="p-4 sm:p-6 md:w-1/2" data-id="element-474">
            <h1 className="text-xl sm:text-2xl font-bold mb-1" data-id="element-475">
              {foodItem.name}
            </h1>
            <p className="text-gray-600 mb-3 sm:mb-4" data-id="element-476">{foodItem.chineseName}</p>
            <div className="flex items-center mb-3 sm:mb-4" data-id="element-477">
              <div className="flex items-center text-yellow-500 mr-2" data-id="element-478">
                <StarIcon size={16} fill="currentColor" className="sm:w-[18px] sm:h-[18px]" data-id="element-479" />
                <span className="ml-1 font-medium" data-id="element-480">{foodItem.rating}</span>
              </div>
              <span className="text-gray-500 text-xs sm:text-sm" data-id="element-481">
                ({foodItem.reviewCount} reviews)
              </span>
            </div>
            <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6" data-id="element-482">
              {foodItem.description}
            </p>
            <div className="mb-4 sm:mb-6" data-id="element-483">
              <h3 className="font-medium mb-2 text-sm sm:text-base" data-id="element-484">
                Ingredients:
              </h3>
              <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-485">
                {foodItem.ingredients.map((ingredient, index) => <span key={index} className="bg-gray-100 text-gray-700 text-xs sm:text-sm px-2 py-1 rounded-full" data-id="element-486">
                    {ingredient}
                  </span>)}
              </div>
            </div>
            <div className="mb-6 sm:mb-8" data-id="element-487">
              <h3 className="font-medium mb-2 text-sm sm:text-base" data-id="element-488">
                Nutritional Information:
              </h3>
              <div className="grid grid-cols-4 gap-1.5 sm:gap-2 text-center" data-id="element-489">
                <div className="bg-gray-50 p-1.5 sm:p-2 rounded" data-id="element-490">
                  <p className="text-xs text-gray-500" data-id="element-491">Calories</p>
                  <p className="font-medium text-xs sm:text-sm" data-id="element-492">
                    {foodItem.nutritionalInfo.calories}
                  </p>
                </div>
                <div className="bg-gray-50 p-1.5 sm:p-2 rounded" data-id="element-493">
                  <p className="text-xs text-gray-500" data-id="element-494">Protein</p>
                  <p className="font-medium text-xs sm:text-sm" data-id="element-495">
                    {foodItem.nutritionalInfo.protein}g
                  </p>
                </div>
                <div className="bg-gray-50 p-1.5 sm:p-2 rounded" data-id="element-496">
                  <p className="text-xs text-gray-500" data-id="element-497">Carbs</p>
                  <p className="font-medium text-xs sm:text-sm" data-id="element-498">
                    {foodItem.nutritionalInfo.carbs}g
                  </p>
                </div>
                <div className="bg-gray-50 p-1.5 sm:p-2 rounded" data-id="element-499">
                  <p className="text-xs text-gray-500" data-id="element-500">Fat</p>
                  <p className="font-medium text-xs sm:text-sm" data-id="element-501">
                    {foodItem.nutritionalInfo.fat}g
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mb-4 sm:mb-6" data-id="element-502">
              <span className="text-xl sm:text-2xl font-bold" data-id="element-503">
                ${foodItem.price.toFixed(2)}
              </span>
              <div className="flex items-center" data-id="element-504">
                <button onClick={handleDecreaseQuantity} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors" aria-label="Decrease quantity" disabled={quantity <= 1} data-id="element-505">
                  <MinusIcon size={14} className="sm:w-4 sm:h-4" data-id="element-506" />
                </button>
                <span className="mx-3 sm:mx-4 font-medium" data-id="element-507">{quantity}</span>
                <button onClick={handleIncreaseQuantity} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors" aria-label="Increase quantity" data-id="element-508">
                  <PlusIcon size={14} className="sm:w-4 sm:h-4" data-id="element-509" />
                </button>
              </div>
            </div>
            {isAdded ? <div className="flex space-x-3" data-id="element-510">
                <Button variant="outline" onClick={() => navigate('/food')} className="flex-1" data-id="element-511">
                  Continue Shopping
                </Button>
                <Button onClick={() => navigate('/cart')} className="flex-1" data-id="element-512">
                  View Cart
                </Button>
              </div> : <Button onClick={handleAddToCart} fullWidth size="lg" className={addAnimation ? 'animate-bounce' : ''} data-id="element-513">
                Add to Cart - ${(foodItem.price * quantity).toFixed(2)}
              </Button>}
          </div>
        </div>
      </div>
      {/* Similar items section */}
      <div className="mt-6 sm:mt-8" data-id="element-514">
        <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4" data-id="element-515">
          You might also like
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-516">
          {[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/food/food-${i + 2}`)} data-id="element-517">
              <img src={foodItem.image} alt="Related food item" className="w-full h-24 sm:h-32 object-cover" data-id="element-518" />
              <div className="p-2 sm:p-3" data-id="element-519">
                <h3 className="font-medium text-xs sm:text-sm" data-id="element-520">
                  {foodItem.name}
                </h3>
                <div className="flex justify-between items-center mt-1 sm:mt-2" data-id="element-521">
                  <span className="font-bold text-xs sm:text-sm" data-id="element-522">
                    ${foodItem.price.toFixed(2)}
                  </span>
                  <button onClick={e => {
                e.stopPropagation();
                addItem({
                  id: `food-${i + 2}`,
                  name: foodItem.name,
                  price: foodItem.price,
                  quantity: 1,
                  image: foodItem.image,
                  chineseName: foodItem.chineseName
                });
              }} className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#E84118] text-white flex items-center justify-center hover:bg-[#d13a16]" aria-label="Add to cart" data-id="element-523">
                    <PlusIcon size={12} className="sm:w-3 sm:h-3" data-id="element-524" />
                  </button>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};
export default FoodDetail;